/**
 * MDJ Curate — UI state machine
 * Maison de Jarin
 *
 * Native custom element, matching the theme's Minimog-base JS convention
 * (class extends HTMLElement + customElements.define — see recon:
 * assets/cart.js). Delegates all blend math to window.MDJCurate.Engine
 * (assets/mdj-curate-engine.js, loaded first) and cart submission to
 * window.MDJCurateCart (assets/mdj-curate-cart.js, loaded after this file).
 *
 * Each step is its own real Shopify page (sections/mdj-curate.liquid's
 * step_mode setting decides which one a given page renders), so this
 * element only ever has ONE step's markup in front of it at a time — no
 * more in-page show/hide switching. State (family/size/selections/name/
 * etc) is persisted to sessionStorage on every mutation (key:
 * mdj_curate_draft) and restored on each new page load, which is what
 * carries a composition forward across the real navigations.
 */

(function () {
  'use strict';

  var DRAFT_KEY = 'mdj_curate_draft';
  var NAME_ALLOWLIST = /^[A-Za-z0-9 '-]*$/;
  var FLOOR_WARNING_G = 3;

  var PROSE_RULES = [
    { when: function (s) { return !!s.leadKey; }, key: 'mdj_curate.prose.led' },
    { when: function (s) { return s.dominantFamily === 'cooling-mint'; }, key: 'mdj_curate.prose.cooling' },
    { when: function (s) { return s.dominantFamily === 'warm-spice'; }, key: 'mdj_curate.prose.warm' },
    { when: function (s) { return s.dominantFamily === 'floral'; }, key: 'mdj_curate.prose.floral' },
    { when: function (s) { return s.dominantFamily === 'earthy-root' || s.dominantFamily === 'smoky-earthy'; }, key: 'mdj_curate.prose.earthy' },
    { when: function () { return true; }, key: 'mdj_curate.prose.balanced' }
  ];

  var PRAISE_RULES = [
    { when: function (s) { return s.leadKey && s.leadName; }, textFn: function (s) { return 'Led by ' + s.leadName + '. A confident choice.'; } },
    { when: function (s) { return s.selections.length >= 5; }, text: 'Few people compose with this much confidence. It will be excellent.' },
    { when: function (s) { return s.selections.length <= 2; }, text: 'Restrained. That is harder than it looks.' },
    { when: function () { return true; }, text: 'Four equals, in balance. The hardest thing to get right.' }
  ];

  function loadJSON(el, selector) {
    var node = el.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class MdjCurateElement extends HTMLElement {
    constructor() {
      super();
      this.ingredients = loadJSON(this, '[data-mdj-curate-ingredients]') || [];
      this.config = loadJSON(this, '[data-mdj-curate-config]') || { families: {}, copy: {}, stepUrls: {} };
      this.engine = window.MDJCurate && window.MDJCurate.Engine;
      this.stepMode = this.dataset.mdjStepMode || 'threshold';

      this.state = {
        family: null,
        sizeGrams: null,
        selections: [], // array of ingredient keys, in selection order
        leadKey: null,
        blendName: '',
        compositionId: null
      };

      this.liveRegion = this.querySelector('[data-mdj-live-region]');
      this.errorToast = this.querySelector('[data-mdj-error-toast]');
    }

    connectedCallback() {
      this._restoreDraft();
      if (this._guardPrerequisites()) return; // navigating away — don't bind/render this page

      switch (this.stepMode) {
        case 'threshold':
          this._bindStep0();
          break;
        case 'family':
          this._bindStep1();
          break;
        case 'size':
          this._bindStep2();
          this._renderStep2();
          break;
        case 'ingredients':
          this._bindStep3();
          this._renderStep3();
          break;
        case 'name':
          this._bindStep4();
          this._renderStep4();
          break;
        case 'sheet':
          this._bindStep5();
          this._renderStep5();
          break;
      }
    }

    // ── Persistence ──────────────────────────────────────────

    _saveDraft() {
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(this.state));
      } catch (e) {
        /* sessionStorage unavailable (private mode / quota) — draft
           persistence is a convenience, not a hard requirement; fail
           silently rather than break the flow. */
      }
    }

    _restoreDraft() {
      try {
        var raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        var saved = JSON.parse(raw);
        if (saved && typeof saved === 'object') {
          this.state = Object.assign(this.state, saved);
        }
      } catch (e) {
        /* corrupt/unavailable draft — start fresh */
      }
    }

    // A page reached with missing prerequisite state — e.g. someone lands
    // on the size page with no family chosen (stale/cleared draft, a
    // bookmarked link, browser back after clearing storage) — sends them
    // back to the earliest step that still makes sense, rather than
    // rendering a broken page. Returns true if it redirected.
    _guardPrerequisites() {
      var urls = this.config.stepUrls || {};
      var need = null;
      if (this.stepMode === 'size' && !this.state.family) need = urls.family;
      else if (this.stepMode === 'ingredients' && (!this.state.family || !this.state.sizeGrams)) need = urls.size;
      else if (this.stepMode === 'name' && (!this.state.family || !this.state.sizeGrams || this.state.selections.length === 0)) need = urls.ingredients;
      else if (this.stepMode === 'sheet' && (!this.state.blendName || !this.state.compositionId)) need = urls.name;
      if (need) {
        window.location.href = need;
        return true;
      }
      return false;
    }

    _goToPage(key) {
      this._saveDraft();
      var url = this.config.stepUrls && this.config.stepUrls[key];
      if (url) window.location.href = url;
    }

    _announce(message) {
      if (this.liveRegion) this.liveRegion.textContent = message;
    }

    _showError(message) {
      if (!this.errorToast) return;
      this.errorToast.textContent = message;
      this.errorToast.hidden = false;
      clearTimeout(this._errorTimer);
      this._errorTimer = setTimeout(function (toast) {
        toast.hidden = true;
      }, 6000, this.errorToast);
    }

    // ── STEP 0 · Threshold ───────────────────────────────────
    // The page is static (no staggered reveal to wait through), so
    // there's nothing to bind here beyond a plain link — this method
    // stays only so connectedCallback's switch doesn't need a special
    // case for step 0.
    _bindStep0() {}

    // ── STEP 1 · Family ──────────────────────────────────────

    _bindStep1() {
      var panels = this.querySelectorAll('[data-mdj-family]');
      panels.forEach((panel) => {
        panel.addEventListener('click', (e) => {
          if (panel.classList.contains('mdj-curate__family-panel--chosen')) return;
          e.preventDefault();
          var family = panel.getAttribute('data-mdj-family');

          // A brief "this one" moment before the cut to the size page — the
          // chosen panel brightens and lifts, the other dims away, instead
          // of an instant flat swap.
          panels.forEach((p) => {
            p.classList.toggle('mdj-curate__family-panel--chosen', p === panel);
            p.classList.toggle('mdj-curate__family-panel--dimmed', p !== panel);
          });

          var delay = reducedMotion() ? 0 : 480;
          setTimeout(() => {
            this.state.family = family;
            this.state.selections = [];
            this.state.leadKey = null;
            this._saveDraft();
            window.location.href = panel.href;
          }, delay);
        });
      });
    }

    // ── STEP 2 · Size ────────────────────────────────────────

    _bindStep2() {
      var continueBtn = this.querySelector('[data-mdj-size-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', () => {
          if (!continueBtn.disabled) this._goToPage('ingredients');
        });
      }
    }

    _renderStep2() {
      var family = this.state.family;
      var familyConfig = this.config.families[family];
      var container = this.querySelector('[data-mdj-size-cards]');
      if (!container || !familyConfig) return;

      container.innerHTML = '';
      var contexts = { 250: 'about 100 cups', 500: 'about 200 cups', 1000: 'about 400 cups', 100: 'about 40 cups', 200: 'about 80 cups' };

      familyConfig.sizes.forEach((size) => {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'mdj-curate__size-card';
        card.setAttribute('aria-pressed', String(this.state.sizeGrams === size.grams));
        card.innerHTML =
          '<span class="mdj-curate__size-card-grams">' + size.grams + ' g</span>' +
          '<span class="mdj-curate__size-card-context">' + escapeHtml(size.grams + ' g — ' + (contexts[size.grams] || '')) + '</span>';
        card.addEventListener('click', () => {
          this.state.sizeGrams = size.grams;
          this._saveDraft();
          this._renderStep2();
        });
        container.appendChild(card);
      });

      // Every family locks a base tea at a fixed share (75% for chai, 50%
      // for green).
      var baseCard = this.querySelector('[data-mdj-base-card]');
      var greenOrientation = this.querySelector('[data-mdj-green-orientation]');
      var baseLabel = this.querySelector('[data-mdj-base-label]');
      if (baseCard) {
        baseCard.hidden = false;
        this.querySelector('[data-mdj-base-name]').textContent = familyConfig.baseName;
        this.querySelector('[data-mdj-base-origin]').textContent = familyConfig.baseOrigin;
        if (baseLabel) {
          baseLabel.textContent = family === 'chai'
            ? this.config.copy.step2BaseLockedLabelChai
            : this.config.copy.step2BaseLockedLabelHerbal;
        }
      }
      if (greenOrientation) greenOrientation.hidden = family === 'chai';

      var continueBtn = this.querySelector('[data-mdj-size-continue]');
      if (continueBtn) continueBtn.disabled = this.state.sizeGrams == null;
    }

    // ── STEP 3 · Composition ─────────────────────────────────

    _bindStep3() {
      this._pinMobileRail();

      var grid = this.querySelector('[data-mdj-ingredient-grid]');
      if (grid) {
        grid.addEventListener('click', (e) => {
          var star = e.target.closest('[data-mdj-lead-star]');
          if (star) {
            e.stopPropagation();
            var card = star.closest('[data-mdj-ingredient-card]');
            this._toggleLead(card.dataset.key);
            return;
          }
          var card = e.target.closest('[data-mdj-ingredient-card]');
          if (card) this._toggleSelection(card.dataset.key);
        });

        // Card and lead star are role="button" elements (a native <button>
        // can't legally contain the star as a second interactive control),
        // so unlike real buttons they don't get Enter/Space activation for
        // free — wire it explicitly to keep the whole grid keyboard-operable.
        grid.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          var star = e.target.closest('[data-mdj-lead-star]');
          if (star) {
            e.preventDefault();
            e.stopPropagation();
            var starCard = star.closest('[data-mdj-ingredient-card]');
            this._toggleLead(starCard.dataset.key);
            return;
          }
          var card = e.target.closest('[data-mdj-ingredient-card]');
          if (card) {
            e.preventDefault();
            this._toggleSelection(card.dataset.key);
          }
        });
      }

      var composeCta = this.querySelector('[data-mdj-compose-cta]');
      if (composeCta) {
        composeCta.addEventListener('click', () => {
          if (!composeCta.disabled) this._goToPage('name');
        });
      }
    }

    // The mobile rail is `position: fixed; bottom: 0`, which anchors to the
    // LAYOUT viewport — but mobile browsers show/hide their address bar as
    // you scroll, which only resizes the smaller VISUAL viewport, not the
    // layout one. The result: the bar visibly slides up and down as the
    // chrome collapses and expands, instead of staying put. Tracking
    // visualViewport directly and nudging the bar to match is the standard
    // fix — it keeps the bar pinned to what's actually on screen.
    _pinMobileRail() {
      var rail = this.querySelector('[data-mdj-rail]');
      if (!rail || !window.visualViewport) return;
      var vv = window.visualViewport;
      var mq = window.matchMedia('(max-width: 750px)');
      var pin = () => {
        if (!mq.matches) {
          rail.style.transform = '';
          return;
        }
        var offset = window.innerHeight - vv.height - vv.offsetTop;
        rail.style.transform = offset > 0.5 ? 'translateY(-' + offset + 'px)' : '';
      };
      vv.addEventListener('resize', pin);
      vv.addEventListener('scroll', pin);
      pin();
    }

    _familyIngredients() {
      var family = this.state.family;
      return this.ingredients.filter(function (ing) {
        return ing.category.indexOf(family) !== -1 && ing.isAvailable;
      });
    }

    _capacityCost(ing) {
      if (ing.isTrace) return 0.08;
      var weight = this.state.family === 'chai' ? ing.weightChai : ing.weightGreen;
      return Math.max(0.08, 0.34 - weight * 0.055);
    }

    _usedCapacity(excludeKey) {
      var all = this._familyIngredients();
      var used = 0;
      this.state.selections.forEach((key) => {
        if (key === excludeKey) return;
        var ing = all.find(function (i) { return i.key === key; });
        if (ing) used += this._capacityCost(ing);
      });
      return used;
    }

    // Whether the CURRENT selection's combined max_share ceilings can
    // cover the required mass. This is a property of the whole set, not
    // any single click — a partial selection (e.g. one ingredient) is
    // expected to read "infeasible" here since the user hasn't finished
    // choosing yet, so this must never gate an individual click (that
    // would wrongly block even the first ingredient). It exists purely
    // to give accurate feedback once the user has reached the minimum
    // count but their combination still can't balance — confirmed
    // reachable in practice: Nutmeg (max_share 0.15) + Cardamom
    // (max_share 0.70) both fit comfortably in the capacity vessel's
    // budget, but their combined ceiling can't cover the spice_mass, so
    // the engine's own guard returns no blend at all.
    _selectionIsFeasible() {
      var all = this._familyIngredients();
      var items = this.state.selections
        .map(function (k) { return all.find(function (i) { return i.key === k; }); })
        .filter(function (i) { return i && !i.isTrace; }); // trace ingredients don't join the proportional pool
      var maxShareSum = items.reduce((sum, i) => {
        return sum + (this.state.family === 'chai' ? i.maxShareChai : i.maxShareGreen);
      }, 0);
      return maxShareSum >= 1 - 1e-9;
    }

    _toggleSelection(key) {
      var idx = this.state.selections.indexOf(key);
      if (idx !== -1) {
        this.state.selections.splice(idx, 1);
        if (this.state.leadKey === key) this.state.leadKey = null;
      } else {
        var all = this._familyIngredients();
        var ing = all.find(function (i) { return i.key === key; });
        if (!ing) return;
        var cost = this._capacityCost(ing);
        if (this._usedCapacity(null) + cost > 1.0 + 1e-9) return; // vessel full — quietly refuse
        this.state.selections.push(key);
      }
      this._saveDraft();
      this._renderStep3();
    }

    _toggleLead(key) {
      var all = this._familyIngredients();
      var ing = all.find(function (i) { return i.key === key; });
      if (!ing || !ing.canLead) return;
      if (this.state.selections.indexOf(key) === -1) return;
      this.state.leadKey = this.state.leadKey === key ? null : key;
      this._saveDraft();
      this._renderStep3();
    }

    _computeBlend() {
      if (!this.engine || !this.state.family || !this.state.sizeGrams) return null;
      var all = this._familyIngredients();
      var selected = this.state.selections
        .map(function (key) { return all.find(function (i) { return i.key === key; }); })
        .filter(Boolean);

      try {
        if (this.state.family === 'chai') {
          var spices = selected.map(function (ing) {
            return { key: ing.key, weight: ing.weightChai, maxShare: ing.maxShareChai, canLead: ing.canLead, isTrace: ing.isTrace };
          });
          return this.engine.computeChaiBlend({
            sizeGrams: this.state.sizeGrams,
            spices: spices,
            leadKey: this.state.leadKey
          });
        }
        // The base tea (Whole Leaf Green Tea / Whole Leaf Black Orthodox)
        // is a fixed 50% share, not a selectable card — it's never in
        // state.selections, so it's found from the full family list, not
        // from `selected`.
        var baseIng = all.find(function (i) { return i.isBase; });
        var herbsForEngine = selected.filter(function (ing) { return !ing.isBase; }).map(function (ing) {
          return { key: ing.key, weight: ing.weightGreen, maxShare: ing.maxShareGreen, canLead: ing.canLead };
        });
        return this.engine.computeGreenBlend({
          sizeGrams: this.state.sizeGrams,
          ingredients: herbsForEngine,
          baseKey: baseIng ? baseIng.key : null,
          leadKey: this.state.leadKey
        });
      } catch (e) {
        // Infeasible combination (combined max_share ceilings can't cover
        // the mass) — the capacity vessel should prevent this in practice;
        // if it doesn't, fail visibly rather than show wrong numbers.
        console.warn('MDJ Curate: blend computation error —', e.message);
        return null;
      }
    }

    // Purely a display label for the same number _capacityCost already
    // enforces — never a second source of truth. Thresholds are read off
    // the real spread of costs across the current library (roughly
    // 0.08–0.31): most ingredients cluster in the middle, a handful of
    // potent ones (cloves, star anise, saffron's trace-mates) sit at the
    // top, and only the highest-weight ones (ginger) sit at the bottom.
    _intensityTier(cost) {
      if (cost >= 0.27) return { tier: 'bold', label: 'Bold' };
      if (cost >= 0.15) return { tier: 'balanced', label: 'Balanced' };
      return { tier: 'gentle', label: 'Gentle' };
    }

    _minimumsMet() {
      if (this.state.family === 'chai') return this.state.selections.length >= 2;
      return this.state.selections.length >= 1; // green: base tea is fixed at 50%, not selectable — >= 1 herb is enough
    }

    _renderStep3() {
      var emptyState = this.querySelector('[data-mdj-empty-state]');
      if (this.ingredients.length === 0) {
        // Live right now: the curate_ingredient metaobjects haven't been
        // seeded yet, so this grid is otherwise silently empty forever
        // with no explanation and a permanently-disabled Compose button.
        if (emptyState) emptyState.hidden = false;
        var composeCtaEmpty = this.querySelector('[data-mdj-compose-cta]');
        if (composeCtaEmpty) composeCtaEmpty.disabled = true;
        return;
      }
      if (emptyState) emptyState.hidden = true;
      var legend = this.querySelector('[data-mdj-intensity-legend]');
      if (legend) legend.hidden = false;

      var all = this._familyIngredients();
      var cards = this.querySelectorAll('[data-mdj-ingredient-card]');
      var used = this._usedCapacity(null);

      cards.forEach((card) => {
        var key = card.dataset.key;
        var ing = all.find(function (i) { return i.key === key; });
        // The base tea (is_base) is locked at a fixed share and is never a
        // selectable card, mirroring chai's base never appearing in the grid.
        var visible = !!ing && !ing.isBase;
        card.hidden = !visible;
        if (!visible) return;

        var selected = this.state.selections.indexOf(key) !== -1;
        card.dataset.mdjSelected = String(selected);
        card.setAttribute('aria-pressed', String(selected));

        var cost = this._capacityCost(ing);
        var wouldOverflow = !selected && used + cost > 1.0 + 1e-9;
        card.dataset.mdjDisabled = String(wouldOverflow);
        var reasonEl = card.querySelector('[data-mdj-card-disabled-reason]');
        if (reasonEl) {
          reasonEl.hidden = !wouldOverflow;
          reasonEl.textContent = wouldOverflow ? 'Would overwhelm the composition.' : '';
        }

        var intensityEl = card.querySelector('[data-mdj-card-intensity]');
        if (intensityEl && !ing.isTrace) {
          var tier = this._intensityTier(cost);
          intensityEl.textContent = tier.label;
          intensityEl.dataset.mdjTier = tier.tier;
          intensityEl.title = tier.tier === 'bold'
            ? 'Uses more of the blend’s capacity — pairs best with one or two others.'
            : tier.tier === 'gentle'
              ? 'Uses little of the blend’s capacity — blends freely with others.'
              : 'Uses a moderate share of the blend’s capacity.';
          intensityEl.hidden = false;
        } else if (intensityEl) {
          // Trace ingredients (a pinch of saffron, etc.) run on their own
          // fixed, negligible share — a Bold/Balanced/Gentle capacity tag
          // doesn't apply to them, so it stays off rather than mislabel.
          intensityEl.hidden = true;
        }

        var star = card.querySelector('[data-mdj-lead-star]');
        if (star) {
          star.hidden = !selected;
          var isLead = this.state.leadKey === key;
          star.toggleAttribute('data-mdj-active', isLead);
          star.setAttribute('aria-pressed', String(isLead));
          star.setAttribute('aria-label', (isLead ? 'Remove ' : 'Make ') + ing.name + (isLead ? ' as lead ingredient' : ' the lead ingredient'));
        }
      });

      var blend = this._computeBlend();
      this._renderVessel(blend, all);
      this._renderRibbon(blend, this.querySelector('[data-mdj-ratio-ribbon]'));
      this._renderProse(blend, all);
      this._updateGramBadges(blend);

      var composeCta = this.querySelector('[data-mdj-compose-cta]');
      var reasonEl = this.querySelector('[data-mdj-compose-reason]');
      var ok = this._minimumsMet() && !!blend;
      if (composeCta) composeCta.disabled = !ok;
      if (reasonEl) {
        var reason = '';
        if (!ok) {
          if (!this._minimumsMet()) {
            reason = this.state.family === 'chai' ? 'Choose at least 2 spices.' : 'Choose at least 1 herb.';
          } else if (!this._selectionIsFeasible()) {
            // Minimum count met, but this specific combination can't balance
            // (e.g. two low max_share spices together) — a different message
            // than "not enough selected," since the count IS enough.
            reason = 'This combination won’t balance — try adding another ingredient or swapping one out.';
          }
        }
        reasonEl.textContent = reason;
      }

      if (blend) {
        var w = blend.warnings || [];
        if (w.length) this._announce(w.map(function (x) { return x.message; }).join(' '));
      }
    }

    _updateGramBadges(blend) {
      var lines = blend ? (blend.spices || blend.herbs || blend.ingredients) : [];
      var cards = this.querySelectorAll('[data-mdj-ingredient-card]');
      cards.forEach((card) => {
        var key = card.dataset.key;
        var badge = card.querySelector('[data-mdj-card-grams]');
        if (!badge) return;
        var line = lines.find(function (l) { return l.key === key; });
        if (line && this.state.selections.indexOf(key) !== -1) {
          badge.hidden = false;
          badge.textContent = line.grams + 'g';
        } else {
          badge.hidden = true;
          badge.textContent = ''; // clear stale grams from a prior valid selection
        }
      });
    }

    _renderVessel(blend, all) {
      var group = this.querySelector('[data-mdj-vessel-fill]');
      var baseLine = this.querySelector('[data-mdj-vessel-base-line]');
      if (!group) return;
      group.innerHTML = '';

      var vesselHeight = 218;
      var vesselWidth = 58;
      var top = 1;

      if (this.state.family === 'chai') {
        if (baseLine) {
          baseLine.hidden = false;
          baseLine.setAttribute('y1', 55);
          baseLine.setAttribute('y2', 55);
        }
        var baseRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        baseRect.setAttribute('x', 1);
        baseRect.setAttribute('y', 55);
        baseRect.setAttribute('width', vesselWidth);
        baseRect.setAttribute('height', vesselHeight - 54);
        baseRect.setAttribute('fill', '#8A7A5E');
        baseRect.setAttribute('opacity', '0.6');
        group.appendChild(baseRect);
        top = 1;
        var spiceHeight = 54;
        var lines = blend ? blend.spices : [];
        var total = this.state.sizeGrams || 1;
        var y = top;
        (lines || []).forEach((line) => {
          var ing = all.find(function (i) { return i.key === line.key; });
          var h = (line.grams / total) * spiceHeight;
          var rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          rect.setAttribute('x', 1);
          rect.setAttribute('y', y);
          rect.setAttribute('width', vesselWidth);
          rect.setAttribute('height', Math.max(0, h));
          rect.setAttribute('fill', (ing && ing.colourHex) || '#C8962A');
          group.appendChild(rect);
          y += h;
        });
      } else {
        // Green: the tea holds a fixed 50% (same structural idea as
        // chai's locked base), herbs fill the rest — herbs stack from the
        // top, the tea renders as a translucent block below, mirroring
        // the chai branch above.
        var total2 = this.state.sizeGrams || 1;
        var baseGrams2 = blend && blend.base ? blend.base.grams : total2 / 2;
        var baseFrac2 = baseGrams2 / total2;
        var herbAreaHeight = vesselHeight * (1 - baseFrac2);
        var baseAreaHeight = vesselHeight - herbAreaHeight;

        if (baseLine) {
          baseLine.hidden = false;
          baseLine.setAttribute('y1', herbAreaHeight + 1);
          baseLine.setAttribute('y2', herbAreaHeight + 1);
        }

        var baseIng2 = blend && blend.base ? all.find(function (i) { return i.key === blend.base.key; }) : null;
        var baseRect2 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        baseRect2.setAttribute('x', 1);
        baseRect2.setAttribute('y', herbAreaHeight + 1);
        baseRect2.setAttribute('width', vesselWidth);
        baseRect2.setAttribute('height', baseAreaHeight);
        baseRect2.setAttribute('fill', (baseIng2 && baseIng2.colourHex) || '#4C6B3F');
        baseRect2.setAttribute('opacity', '0.6');
        group.appendChild(baseRect2);

        var y2 = top;
        var lines2 = blend ? blend.herbs : [];
        (lines2 || []).forEach((line) => {
          var ing = all.find(function (i) { return i.key === line.key; });
          var h2 = (line.grams / total2) * herbAreaHeight;
          var rect2 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          rect2.setAttribute('x', 1);
          rect2.setAttribute('y', y2);
          rect2.setAttribute('width', vesselWidth);
          rect2.setAttribute('height', Math.max(0, h2));
          rect2.setAttribute('fill', (ing && ing.colourHex) || '#C8962A');
          group.appendChild(rect2);
          y2 += h2;
        });
      }
    }

    _renderRibbon(blend, ribbonEl) {
      if (!ribbonEl) return;
      ribbonEl.innerHTML = '';
      if (!blend) return;
      var lines = blend.spices || blend.herbs || blend.ingredients || [];
      var all = this._familyIngredients();
      var total = this.state.sizeGrams || 1;

      if (blend.base) {
        var baseSpan = document.createElement('span');
        baseSpan.style.width = ((blend.base.grams / total) * 100) + '%';
        if (this.state.family === 'chai') {
          baseSpan.style.background = '#8A7A5E';
        } else {
          var baseIngForRibbon = all.find(function (i) { return i.key === blend.base.key; });
          baseSpan.style.background = (baseIngForRibbon && baseIngForRibbon.colourHex) || '#4C6B3F';
        }
        ribbonEl.appendChild(baseSpan);
      }
      lines.forEach((line) => {
        var ing = all.find(function (i) { return i.key === line.key; });
        var span = document.createElement('span');
        span.style.width = ((line.grams / total) * 100) + '%';
        span.style.background = (ing && ing.colourHex) || '#C8962A';
        ribbonEl.appendChild(span);
      });
    }

    // Sheet's composition bloom — one stem per row, radiating from a
    // central hub, tipped with a dot in the exact same colourHex each
    // row's card swatch already used back in Blend. Colour, length and
    // name are one visual unit per ingredient (no shared ring to
    // decode), so there's no separate legend to cross-reference. Takes
    // the same `rows` _renderStep5 already built for the table
    // (grams/pct/colourHex per ingredient) rather than recomputing
    // anything from the blend.
    _renderSheetBloom(rows, totalGrams, svgEl, totalEl) {
      if (!svgEl) return;
      var ns = 'http://www.w3.org/2000/svg';
      var spokesG = svgEl.querySelector('[data-mdj-sheet-bloom-spokes]');
      if (totalEl) totalEl.textContent = totalGrams + ' g';
      if (!spokesG) return;
      spokesG.innerHTML = '';
      if (!rows.length || !totalGrams) return;

      var cx = 120, cy = 120;
      var hubR = 34; // matches .mdj-curate__sheet-bloom-hub's r — stems start at its edge, never inside it
      var maxLen = 78;
      var n = rows.length;
      var maxPct = rows.reduce(function (m, row) { return Math.max(m, row.pct); }, 0) || 1;

      rows.forEach((row, i) => {
        // Even angular spacing (not proportional, like a pie) — every
        // ingredient gets its own uncrowded direction to grow a label
        // in, regardless of how small its share is. Share is read from
        // stem LENGTH instead.
        var angle = (i * 2 * Math.PI) / n; // 0 = 12 o'clock, clockwise
        var sinA = Math.sin(angle), cosA = Math.cos(angle);
        // Floor of 0.3 so even a trace ingredient's stem — and its dot
        // and label — stay visibly present, not shrunk to nothing next
        // to the dominant one.
        var lenFrac = 0.3 + 0.7 * (row.pct / maxPct);
        var len = hubR + (maxLen - hubR) * lenFrac;
        var startX = cx + hubR * sinA, startY = cy - hubR * cosA;
        var tipX = cx + len * sinA, tipY = cy - len * cosA;
        var color = row.colourHex || '#C8962A';

        // A gentle perpendicular bow, alternating side by index, so
        // stems read as growing rather than as ruled measurement lines.
        var bow = (len - hubR) * 0.12 * (i % 2 === 0 ? 1 : -1);
        var midX = (startX + tipX) / 2 + bow * cosA;
        var midY = (startY + tipY) / 2 + bow * sinA;

        var g = document.createElementNS(ns, 'g');
        g.setAttribute('class', 'mdj-curate__sheet-bloom-stem-group');

        var stem = document.createElementNS(ns, 'path');
        stem.setAttribute('d', 'M' + startX + ',' + startY + ' Q' + midX + ',' + midY + ' ' + tipX + ',' + tipY);
        stem.setAttribute('class', 'mdj-curate__sheet-bloom-stem');
        stem.setAttribute('stroke', color);
        g.appendChild(stem);

        var dot = document.createElementNS(ns, 'circle');
        dot.setAttribute('cx', tipX);
        dot.setAttribute('cy', tipY);
        dot.setAttribute('r', 3.5 + 3.5 * (row.pct / maxPct));
        dot.setAttribute('fill', color);
        dot.setAttribute('class', 'mdj-curate__sheet-bloom-dot');
        g.appendChild(dot);

        // A fixed outer radius for every label — independent of that
        // row's own stem length — so the dominant ingredient's stem
        // (the one reaching closest to this ring) never lands its own
        // label back on top of its dot. Labels always sit on one clean
        // ring; only the stems/dots inside it vary by share.
        var labelR = maxLen + 22;
        var lx = cx + labelR * sinA, ly = cy - labelR * cosA;
        var anchor = sinA > 0.2 ? 'start' : (sinA < -0.2 ? 'end' : 'middle');
        var text = document.createElementNS(ns, 'text');
        text.setAttribute('x', lx);
        text.setAttribute('y', ly);
        text.setAttribute('text-anchor', anchor);
        text.setAttribute('class', 'mdj-curate__sheet-bloom-label');
        var nameTspan = document.createElementNS(ns, 'tspan');
        nameTspan.setAttribute('x', lx);
        nameTspan.textContent = row.name.split(' ')[0];
        var pctTspan = document.createElementNS(ns, 'tspan');
        pctTspan.setAttribute('x', lx);
        pctTspan.setAttribute('dy', '1.2em');
        pctTspan.setAttribute('class', 'mdj-curate__sheet-bloom-pct');
        pctTspan.textContent = Math.round(row.pct) + '%';
        text.appendChild(nameTspan);
        text.appendChild(pctTspan);
        g.appendChild(text);

        spokesG.appendChild(g);
      });
    }

    _dominantTastingFamily(blend, all) {
      if (!blend) return null;
      var lines = blend.spices || blend.herbs || blend.ingredients || [];
      var tally = {};
      lines.forEach((line) => {
        var ing = all.find(function (i) { return i.key === line.key; });
        if (!ing) return;
        tally[ing.tastingFamily] = (tally[ing.tastingFamily] || 0) + line.grams;
      });
      var best = null;
      var bestVal = -1;
      Object.keys(tally).forEach(function (k) {
        if (tally[k] > bestVal) { best = k; bestVal = tally[k]; }
      });
      return best;
    }

    _renderProse(blend, all) {
      var el = this.querySelector('[data-mdj-prose-line]');
      if (!el) return;
      if (!blend) { el.textContent = ''; return; }
      var dominant = this._dominantTastingFamily(blend, all);
      var rule = PROSE_RULES.find((r) => r.when({ leadKey: this.state.leadKey, dominantFamily: dominant }));
      var text = (window.MDJCurateStrings && window.MDJCurateStrings[rule.key]) || 'A composition in balance.';
      el.textContent = text;
    }

    // ── STEP 4 · Naming ──────────────────────────────────────

    _bindStep4() {
      var input = this.querySelector('[data-mdj-name-input]');
      var confirmBtn = this.querySelector('[data-mdj-confirm-name]');
      if (input) {
        input.addEventListener('input', () => {
          // Filter first, then truncate — otherwise disallowed characters
          // (which get stripped anyway) eat into the 32-char budget,
          // leaving fewer than 32 valid characters in the final name.
          var value = input.value;
          if (!NAME_ALLOWLIST.test(value)) {
            value = value.replace(/[^A-Za-z0-9 '-]/g, '');
          }
          value = value.slice(0, 32);
          input.value = value;
          this.state.blendName = value;
          if (confirmBtn) confirmBtn.disabled = value.trim().length === 0;
          this._saveDraft();
        });
      }
      if (confirmBtn) {
        confirmBtn.addEventListener('click', () => {
          if (!this.state.blendName || !NAME_ALLOWLIST.test(this.state.blendName)) return;
          this.state.compositionId = 'MDJ-' + Math.floor(1000 + Math.random() * 9000);
          this._goToPage('sheet');
        });
      }
    }

    _renderStep4() {
      var input = this.querySelector('[data-mdj-name-input]');
      if (input && this.state.blendName) input.value = this.state.blendName;

      var all = this._familyIngredients();
      var blend = this._computeBlend();
      var dominant = this._dominantTastingFamily(blend, all);
      var leadIng = all.find((i) => i.key === this.state.leadKey);
      var chipsEl = this.querySelector('[data-mdj-name-chips]');
      if (!chipsEl) return;

      var suggestions = [];
      if (this.state.family === 'chai') {
        suggestions.push('Idukki Morning');
      } else {
        suggestions.push('Kannauj Dusk');
      }
      if (leadIng) suggestions.push(leadIng.name + (this.state.family === 'chai' ? ' Hour' : ' Garden'));
      else if (dominant) suggestions.push(dominant.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }));
      suggestions.push('The Sixth Hour');

      chipsEl.innerHTML = suggestions.slice(0, 3).map(function (s) {
        return '<button type="button" class="mdj-curate__name-chip">' + escapeHtml(s) + '</button>';
      }).join('');
      chipsEl.querySelectorAll('button').forEach((chip) => {
        chip.addEventListener('click', () => {
          // Same sanitizing the typed-input handler applies — a suggestion
          // built from an ingredient/tasting-family name could contain
          // characters outside NAME_ALLOWLIST (accents, "&", parentheses),
          // and without this the Continue button silently no-ops.
          var value = chip.textContent;
          if (!NAME_ALLOWLIST.test(value)) {
            value = value.replace(/[^A-Za-z0-9 '-]/g, '');
          }
          value = value.slice(0, 32);
          if (input) input.value = value;
          this.state.blendName = value;
          var confirmBtn = this.querySelector('[data-mdj-confirm-name]');
          if (confirmBtn) confirmBtn.disabled = value.trim().length === 0;
          this._saveDraft();
        });
      });
    }

    // ── STEP 5 · Blend sheet ─────────────────────────────────

    _bindStep5() {
      var addToCart = this.querySelector('[data-mdj-add-to-cart]');
      if (addToCart) {
        addToCart.addEventListener('click', () => {
          // A stale sessionStorage draft can land here with an ingredient
          // that's since gone unavailable — re-check right before adding
          // rather than trusting whatever _renderStep5 last computed.
          if (addToCart.disabled || !this._computeBlend()) {
            this._showError('This composition can no longer be completed — please adjust your selections.');
            return;
          }
          if (window.MDJCurateCart) {
            window.MDJCurateCart.add(this._buildCartPayload(), {
              onError: (msg) => this._showError(msg),
              onSuccess: () => {
                this._announce('Added to cart.');
                try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) {}
              }
            });
          }
        });
      }

      var download = this.querySelector('[data-mdj-download-sheet]');
      if (download) {
        download.addEventListener('click', () => this._downloadSheet());
      }
    }

    _buildCartPayload() {
      var all = this._familyIngredients();
      var blend = this._computeBlend();
      var lines = blend ? (blend.spices || blend.herbs || blend.ingredients) : [];
      var variantId = this._currentVariantId();

      var familyConfig = this.config.families[this.state.family];
      var compositionParts = [];
      if (blend && blend.base) compositionParts.push((familyConfig.baseName) + ' ' + blend.base.grams + 'g');
      lines.forEach((line) => {
        var ing = all.find(function (i) { return i.key === line.key; });
        compositionParts.push((ing ? ing.name : line.key) + ' ' + line.grams + 'g');
      });

      var recipe = lines.map((line) => ({
        handle: line.key,
        grams: line.grams,
        pct: Number(line.pct.toFixed(1)),
        clamped: !!line.clamped
      }));
      if (blend && blend.base) recipe.unshift({ handle: 'base', grams: blend.base.grams, pct: Number(blend.base.pct.toFixed(1)), clamped: false });

      return {
        variantId: variantId,
        properties: {
          'Blend Name': this.state.blendName,
          'Composition': compositionParts.join(' · '),
          'Profile': this.querySelector('[data-mdj-prose-line]') ? this.querySelector('[data-mdj-prose-line]').textContent : '',
          '_family': this.state.family,
          '_size_g': String(this.state.sizeGrams),
          '_lead': this.state.leadKey || '',
          '_recipe_json': JSON.stringify(recipe),
          '_composition_id': this.state.compositionId || ''
        }
      };
    }

    _currentVariantId() {
      var familyConfig = this.config.families[this.state.family];
      if (!familyConfig) return null;
      var size = familyConfig.sizes.find((s) => s.grams === this.state.sizeGrams);
      return size ? size.variantId : null;
    }

    _renderStep5() {
      var all = this._familyIngredients();
      var blend = this._computeBlend();
      var addToCart = this.querySelector('[data-mdj-add-to-cart]');
      if (!blend) {
        // Reachable if a sessionStorage draft resumes straight onto this
        // step after an ingredient it relied on became unavailable — leave
        // whatever was last rendered (rather than a half-built blank sheet)
        // but make sure the customer can't buy an empty/undefined blend.
        if (addToCart) addToCart.disabled = true;
        this._showError('This composition can no longer be completed — please adjust your selections.');
        return;
      }
      if (addToCart) addToCart.disabled = false;

      this.querySelector('[data-mdj-sheet-composition-no]').textContent = 'Composition Nº ' + (this.state.compositionId || '').replace('MDJ-', '');
      this.querySelector('[data-mdj-sheet-name]').textContent = this.state.blendName;

      var familyLabel = this.state.family === 'chai' ? 'A chai composition' : 'A green tea composition';
      var dateStr = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      this.querySelector('[data-mdj-sheet-meta]').textContent = familyLabel + ' · ' + this.state.sizeGrams + ' g · ' + dateStr;

      var leadIng = all.find((i) => i.key === this.state.leadKey);
      var leadEl = this.querySelector('[data-mdj-sheet-lead]');
      if (leadIng) {
        leadEl.hidden = false;
        leadEl.textContent = 'LED BY ' + leadIng.name.toUpperCase();
      } else {
        leadEl.hidden = true;
      }

      var tbody = this.querySelector('[data-mdj-sheet-table-body]');
      tbody.innerHTML = '';
      var sheetFamilyConfig = this.config.families[this.state.family];
      var rows = [];
      if (blend.base) {
        // For green the base IS a real ingredient (its own metaobject,
        // own photo); for chai the base is a house setting with no
        // single metaobject behind it, so no image is available.
        var baseIng = blend.base.key ? all.find(function (i) { return i.key === blend.base.key; }) : null;
        rows.push({ name: sheetFamilyConfig.baseName, hindiName: baseIng ? baseIng.hindiName : '', origin: sheetFamilyConfig.baseOrigin, grams: blend.base.grams, pct: blend.base.pct, trace: false, image: baseIng ? baseIng.image : '', colourHex: baseIng ? baseIng.colourHex : '#8A7A5E' });
      }
      (blend.spices || blend.herbs || blend.ingredients || []).forEach((line) => {
        var ing = all.find(function (i) { return i.key === line.key; });
        rows.push({ name: ing ? ing.name : line.key, hindiName: ing ? ing.hindiName : '', origin: ing ? ing.origin : '', grams: line.grams, pct: line.pct, trace: ing ? ing.isTrace : false, image: ing ? ing.image : '', colourHex: ing ? ing.colourHex : '#C8962A' });
      });

      var totalGrams = rows.reduce(function (s, r) { return s + r.grams; }, 0);
      this.querySelector('[data-mdj-sheet-total-grams]').textContent = totalGrams + ' g';

      rows.forEach((row, i) => {
        var tr = document.createElement('tr');
        tr.className = 'mdj-curate__sheet-row';
        tr.style.transitionDelay = (i * 60) + 'ms';
        var thumb = row.image
          ? '<img src="' + escapeHtml(row.image) + '" alt="" loading="lazy" class="mdj-curate__sheet-thumb-img">'
          : '<span class="mdj-curate__sheet-thumb-swatch" style="background:' + escapeHtml(row.colourHex || '#C8962A') + '"></span>';
        var nameCell = escapeHtml(row.name.toUpperCase())
          + (row.hindiName ? ' <span class="mdj-curate__sheet-hindi" lang="hi">' + escapeHtml(row.hindiName) + '</span>' : '')
          // Origin repeats here as a small subtitle for narrow screens,
          // where a 5th table column has no room — see .mdj-curate__sheet-cell-origin,
          // which hides one copy or the other by width, never both.
          + '<span class="mdj-curate__sheet-cell-origin-inline">' + escapeHtml(row.origin) + '</span>';
        tr.innerHTML =
          '<td class="mdj-curate__sheet-cell-thumb"><span class="mdj-curate__sheet-thumb">' + thumb + '</span></td>' +
          '<td class="mdj-curate__sheet-cell-name">' + nameCell + '</td>' +
          '<td class="mdj-curate__sheet-cell-origin">' + escapeHtml(row.origin) + '</td>' +
          '<td class="mdj-curate__sheet-cell-grams">' + (row.trace ? row.grams + ' g trace' : row.grams + ' g') + '</td>' +
          '<td class="mdj-curate__sheet-cell-pct">' + row.pct.toFixed(1) + '%</td>';
        tbody.appendChild(tr);
      });

      if (reducedMotion()) {
        tbody.querySelectorAll('.mdj-curate__sheet-row').forEach(function (r) { r.setAttribute('data-mdj-shown', 'true'); });
      } else {
        requestAnimationFrame(function () {
          tbody.querySelectorAll('.mdj-curate__sheet-row').forEach(function (r) {
            setTimeout(function () { r.setAttribute('data-mdj-shown', 'true'); }, parseInt(r.style.transitionDelay, 10) || 0);
          });
        });
      }

      this._renderSheetBloom(rows, totalGrams, this.querySelector('[data-mdj-sheet-bloom]'), this.querySelector('[data-mdj-sheet-wheel-total]'));
      var dominant = this._dominantTastingFamily(blend, all);
      var proseRule = PROSE_RULES.find((r) => r.when({ leadKey: this.state.leadKey, dominantFamily: dominant }));
      this.querySelector('[data-mdj-sheet-prose]').textContent =
        (window.MDJCurateStrings && window.MDJCurateStrings[proseRule.key]) || '';

      this.querySelector('[data-mdj-sheet-method]').textContent =
        this.state.family === 'chai'
          ? '6 g per 200 ml · 95°C · 4 minutes · takes milk'
          : '2 g per 200 ml · 80°C · 3 minutes';

      var praiseContext = { leadKey: this.state.leadKey, leadName: leadIng ? leadIng.name : null, selections: this.state.selections, family: this.state.family };
      var praiseRule = PRAISE_RULES.find((r) => r.when(praiseContext));
      this.querySelector('[data-mdj-sheet-praise]').textContent = praiseRule.text || praiseRule.textFn(praiseContext);

      var warnings = blend.warnings || [];
      if (warnings.length) this._announce(warnings.map(function (w) { return w.message; }).join(' '));
    }

    _downloadSheet() {
      var canvas = this.querySelector('[data-mdj-sheet-canvas]');
      if (!canvas) return;
      var ctx = canvas.getContext('2d');
      var w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#F5EDD8';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = '#C8962A';
      ctx.lineWidth = 2;
      ctx.strokeRect(24, 24, w - 48, h - 48);

      ctx.fillStyle = '#1a140a';
      ctx.font = '24px Georgia';
      ctx.fillText('MAISON DE JARIN', 60, 90);
      ctx.font = 'italic 56px Georgia';
      ctx.fillText(this.state.blendName || '', 60, 170);

      var rows = Array.from(this.querySelectorAll('[data-mdj-sheet-table-body] tr'));
      var y = 260;
      ctx.font = '22px Georgia';
      rows.forEach((tr) => {
        var cells = tr.querySelectorAll('td');
        var text = cells[1].textContent + '  —  ' + cells[3].textContent + '  (' + cells[4].textContent + ')';
        ctx.fillText(text, 60, y);
        y += 40;
      });

      ctx.font = 'italic 20px Georgia';
      ctx.fillText(this.querySelector('[data-mdj-sheet-prose]').textContent, 60, y + 40);
      ctx.font = '14px sans-serif';
      ctx.fillText('Composed by you. Blended in Amritsar.', 60, h - 60);

      var link = document.createElement('a');
      link.download = (this.state.blendName || 'blend-sheet').replace(/[^a-z0-9-]+/gi, '-') + '.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  }

  if (!customElements.get('mdj-curate')) {
    customElements.define('mdj-curate', MdjCurateElement);
  }
})();
